import { Navigate, useRoutes } from 'react-router-dom';
import React, { Suspense, lazy } from 'react';
import PrivateRoutes from './components/PrivateRoutes';
import Loader from './components/Loader';
import TrialUsers from './pages/settings/users/TrialUsers';
import EvaluationCriteria from './pages/settings/candidate-settings/EvaluationCriteria';
import ViewedProfiles from './pages/hire/ViewedProfiles';

// Lazy load all page components
const DashboardLayout = lazy(() => import('./layouts/dashboard'));
const LogoOnlyLayout = lazy(() => import('./layouts/LogoOnlyLayout'));
const Login = lazy(() => import('./pages/Login'));
const NotFound = lazy(() => import('./pages/Page404'));
const DashboardApp = lazy(() => import('./pages/DashboardApp'));
const CareerSite = lazy(() => import('./pages/settings/career-site/CareerSite'));
const List = lazy(() => import('./pages/settings/users/List'));
const Approvals = lazy(() => import('./pages/settings/users/Approvals'));
const NewHiringPipeline = lazy(() => import('./pages/settings/hiring-pipeline/NewHiringPipeline'));
const Categories = lazy(() => import('./pages/settings/email-templates/Categories'));
const Templates = lazy(() => import('./pages/settings/email-templates/Templates'));
const Webforms = lazy(() => import('./pages/settings/candidate-settings/Webforms'));
const AssessmentCategories = lazy(() => import('./pages/settings/candidate-settings/AssessmentCategories'));
const JobBoards = lazy(() => import('./pages/settings/job-boards/JobBoards'));
const Billing = lazy(() => import('./pages/settings/billing/Billing'));
const Jobs = lazy(() => import('./pages/main/jobs/Jobs'));
const CreateJob = lazy(() => import('./pages/main/jobs/CreateJob'));
const Candidates = lazy(() => import('./pages/main/candidates/Candidates'));
const Interviews = lazy(() => import('./pages/main/interviews/Interviews'));
const Assessments = lazy(() => import('./pages/main/assessments/Assessments'));
const CreateAssessment = lazy(() => import('./pages/main/assessments/CreateAssessment'));
const AssesmentPreview = lazy(() => import('./pages/main/assessments/AssesmentPreview'));
const AddUser = lazy(() => import('./components/users/AddUser'));
const NewIS = lazy(() => import('./pages/settings/institute/NewIS'));
const CreateEmail = lazy(() => import('./pages/settings/email-templates/CreateEmail'));
const DashboardWithoutSidebar = lazy(() => import('./layouts/dashboard/DashboardwithoutSidebar'));
const NewcreateCandidate = lazy(() => import('./pages/main/CreateCandidate/NewcreateCandidate'));
const InstitueAddress = lazy(() => import('./pages/settings/institute/InstitueAddress'));
const SearchCandidates = lazy(() => import('./pages/hire/SearchCandidates'));
const SearchResults = lazy(() => import('./pages/hire/SearchResults'));
const PerticularCandidate = lazy(() => import('./pages/main/candidates/PerticularCandidate'));
const PerticularJob = lazy(() => import('./pages/main/jobs/PerticularJob'));
const Contacts = lazy(() => import('./pages/settings/contacts/Contacts'));
const PriviewInterview = lazy(() => import('./pages/main/interviews/PriviewInterview'));
const EmailSmtp = lazy(() => import('./pages/settings/email-templates/EmailSmtp'));
const ActivateAccount = lazy(() => import('./pages/ActivateAccount'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ChangePassword = lazy(() => import('./pages/settings/users/ChangePassword'));
const ModulesFormBuilder = lazy(() => import('./pages/settings/candidate-settings/ModulesFormBuilder'));
const Modules = lazy(() => import('./pages/settings/modules/Modules'));
const WebformBuilder = lazy(() => import('./pages/settings/candidate-settings/WebformBuilder'));
const PerticularTemplateJob = lazy(() => import('./pages/main/jobs/PerticularTemplateJob'));
const NewCreateInterview = lazy(() => import('./pages/main/CreateNewInteview/NewCreateInterview'));
const InterviewReschedule = lazy(() => import('./pages/interview-reschedule/InterviewReschedule'));
const InterviewFeedback = lazy(() => import('./pages/interview-reschedule/InterviewFeedback'));
const InterviewerReschedule = lazy(() => import('./pages/interview-reschedule/InterviewerReschedule'));

// ----------------------------------------------------------------------

export default function Router() {
  return (
    <Suspense fallback={<Loader />}>
      {useRoutes([
        {
          path: "/login",
          element: <Login />
        },
        {
          path: "/auth/activate/:token",
          element: <ActivateAccount />
        },
        {
          path: "/auth/forgot-password/",
          element: <ForgotPassword />
        },
        {
          path: "/auth/reset-password/:token",
          element: <ResetPassword />
        },
        {
          path: 'headless/:interviewId/:user_type/:token',
          element: <InterviewReschedule />
        },
        {
          path: 'headless/reschedule/:pk/',
          element: <InterviewerReschedule />
        },
        {
          path: 'headless/feedback/:interviewId/:interviewr_id/:candidate_id',
          element: <InterviewFeedback />
        },

        {
          path: '/dashboard',
          element: (
            <PrivateRoutes>
              <DashboardLayout />
            </PrivateRoutes>
          ),
          children: [
            { path: '/dashboard/user/adduser', element: <AddUser /> },
            { path: 'users/list', element: <List /> },
            { path: 'users/approvals', element: <Approvals /> },
            { path: 'users/trial-users', element: <TrialUsers /> },
            { path: 'users/change-password', element: <ChangePassword /> },
            
            //  {New Institute Settings page(combined)}
            { path: '/dashboard/InstituteSettings/settings', element: <NewIS /> },
            { path: '/dashboard/InstituteSettings/address', element: <InstitueAddress /> },
            { path: '/dashboard/createEmail', element: <CreateEmail /> },
            { path: '/dashboard/updateEmail/:id', element: <CreateEmail /> },
            { path: '/dashboard/contacts', element: <Contacts /> },
            { path: 'email-templates/templates', element: <Templates /> },
            { path: '/dashboard/candidate-settings/webforms', element: <Webforms /> },
            { path: '/dashboard/candidate-settings/modules', element: <Modules /> },
            { path: '/dashboard/job-boards', element: <JobBoards /> },
            { path: '/dashboard/bills', element: <Billing /> },
            { path: 'candidate-settings/assessment-categories', element: <AssessmentCategories /> },
            { path: 'email-templates/categories', element: <Categories /> },
            { path: 'email-templates/emailsetting', element: <EmailSmtp /> },
            { path: '/dashboard/candidate-settings/webforms/new', element: <WebformBuilder /> },
            { path: '/dashboard/candidate-settings/webforms/update/:id', element: <WebformBuilder /> },
            { path: '/dashboard/candidate-settings/modules/new', element: <ModulesFormBuilder /> },
            { path: '/dashboard/candidate-settings/modules/update/:id', element: <ModulesFormBuilder /> },
            { path: '/dashboard/career-site', element: <CareerSite /> },
            { path: '/dashboard/candidate-evaluation', element: <EvaluationCriteria /> },
          ],
        },
        {
          path: '/',
          element: (
            <PrivateRoutes>
              <LogoOnlyLayout />
            </PrivateRoutes>
          ),
          children: [
            { path: '/dasboard/app', element: <DashboardApp /> },
            { path: '404', element: <NotFound /> },
            { path: '*', element: <Navigate to="/404" /> },
          ],
        },
        {
          path: '/dashboard',
          element: (
            <PrivateRoutes>
              <DashboardWithoutSidebar />
            </PrivateRoutes>
          ),
          children: [

            { path: '/dashboard/candidates/newcreate', element: <NewcreateCandidate /> },
            { path: '/dashboard/candidates/edit-candidate/:editCandidateId', element: <NewcreateCandidate /> },
            { path: '/dashboard/NewInterview', element: <NewCreateInterview /> },
            { path: '/dashboard/candidate/perticularCandidate/:id', element: <PerticularCandidate /> },
            { path: '/dashboard/candidate/jobDetails/:id', element: <PerticularJob /> },
            { path: '/dashboard/candidate/templateJobDetails/:id', element: <PerticularTemplateJob /> },
            { path: '/dashboard/app', element: <DashboardApp /> },
            { path: '/dashboard/jobs', element: <Jobs /> },
            { path: '/dashboard/assessments/create-assessment', element: <CreateAssessment /> },
            { path: '/dashboard/assessments/assessment-preview', element: <AssesmentPreview /> },
            { path: '/dashboard/jobs/create-job', element: <CreateJob /> },
            { path: '/dashboard/jobs/edit-job/:editJobId', element: <CreateJob /> },
            { path: '/dashboard/candidates', element: <Candidates /> },
            { path: 'hiring-pipeline/pipelines', element: <NewHiringPipeline /> },
            { path: 'hiring-pipeline/new-pipelines', element: <NewHiringPipeline /> },
            { path: '/dashboard/interviews', element: <Interviews /> },
            { path: '/dashboard/interviews/edit-interview/:id', element: <NewCreateInterview /> },
            { path: '/dashboard/interviews/perticularInterview/:id', element: <PriviewInterview /> },
            { path: '/dashboard/assessments', element: <Assessments /> },
            { path: '/dashboard/assessments/edit-assessment/:id', element: <CreateAssessment /> },
            { path: '/dashboard/candidate-search', element: <SearchCandidates /> },
            { path: '/dashboard/search-results', element: <SearchResults /> },
            { path: "/dashboard/viewed-profiles", element: <ViewedProfiles /> }
          ],
        },
        { path: '*', element: <Navigate to="/404" replace /> },
      ])}
    </Suspense>
  )
}